import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useNavFlags } from "@/hooks/use-nav-flags";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { navFlagKeys, navItemVisible, type NavConfig, type NavItem } from "@/config/navigation";
import type { SidebarColorScheme } from "@/components/layout/SidebarNav";

interface CommandPaletteProps {
  config: NavConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  colorScheme?: SidebarColorScheme;
}

export function CommandPalette({ config, open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const flags = useNavFlags(navFlagKeys(config));

  // Atalho Cmd+K / Ctrl+K
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  function getVisibleItems(): Array<{ group: string; item: NavItem }> {
    return config.groups.flatMap((g) =>
      g.items
        .filter((item) => navItemVisible(item, { email: user?.email, flags }))
        .map((item) => ({ group: g.label, item }))
    );
  }

  function handleSelect(route: string) {
    onOpenChange(false);
    navigate(route);
  }

  const visible = getVisibleItems();
  const byGroup = config.groups
    .map((g) => ({
      label: g.label,
      items: g.items.filter((item) => navItemVisible(item, { email: user?.email, flags })),
    }))
    .filter((g) => g.items.length > 0);

  if (visible.length === 0) return null;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Buscar página..." aria-label="Buscar páginas do menu" />
      <CommandList>
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
        {byGroup.map((group) => (
          <CommandGroup key={group.label} heading={group.label}>
            {group.items.map((item) => (
              <CommandItem
                key={item.id}
                value={item.label}
                onSelect={() => handleSelect(item.route)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <item.icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
